export type Vertex = { x: number; y: number };

export const MAX_DECK_FT = 50;

export function rectangleVertices(length: number, width: number): Vertex[] {
  const hx = length / 2;
  const hy = width / 2;
  return [
    { x: -hx, y: -hy },
    { x: hx, y: -hy },
    { x: hx, y: hy },
    { x: -hx, y: hy },
  ];
}

export function edgeLength(a: Vertex, b: Vertex): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

export function perimeter(v: Vertex[]): number {
  let s = 0;
  for (let i = 0; i < v.length; i++) {
    s += edgeLength(v[i], v[(i + 1) % v.length]);
  }
  return s;
}

export function polygonSignedArea(v: Vertex[]): number {
  let s = 0;
  for (let i = 0; i < v.length; i++) {
    const a = v[i];
    const b = v[(i + 1) % v.length];
    s += a.x * b.y - b.x * a.y;
  }
  return s / 2;
}

export function polygonArea(v: Vertex[]): number {
  return Math.abs(polygonSignedArea(v));
}

export function bbox(v: Vertex[]) {
  const xs = v.map((p) => p.x);
  const ys = v.map((p) => p.y);
  return {
    minX: Math.min(...xs),
    maxX: Math.max(...xs),
    minY: Math.min(...ys),
    maxY: Math.max(...ys),
  };
}

export function snapPt(p: Vertex, step = 0.5): Vertex {
  return {
    x: Math.round(p.x / step) * step,
    y: Math.round(p.y / step) * step,
  };
}

// Pad/trim a railedEdges array to length n. Missing entries default to true.
export function normalizeRailedEdges(
  prev: boolean[] | undefined,
  n: number,
): boolean[] {
  const out: boolean[] = [];
  for (let i = 0; i < n; i++) out.push(prev?.[i] ?? true);
  return out;
}

const RECT_EDGE_LABELS = ["Front", "Right", "Back", "Left"];

export function edgeName(v: Vertex[], i: number): string {
  if (v.length === 4) return RECT_EDGE_LABELS[i] ?? `Side ${i + 1}`;
  return `Side ${i + 1}`;
}

export function fmtFt(value: number): string {
  if (Number.isInteger(value)) return `${value}'`;
  return `${value.toFixed(1)}'`;
}

export type StairGeom = {
  edgeIndex: number;
  midX: number;
  midY: number;
  tx: number;
  ty: number;
  ox: number;
  oy: number;
  edgeLen: number;
  stairWidth: number;
  stairLength: number;
};

export type StairsConfig = {
  enabled: boolean;
  edgeIndex: number;
  lengthFt: number;
  withRailing: boolean;
};

export function getStairGeometry(
  v: Vertex[],
  stairs: StairsConfig,
): StairGeom | null {
  if (!stairs.enabled) return null;
  const i = Math.min(stairs.edgeIndex, v.length - 1);
  const a = v[i];
  const b = v[(i + 1) % v.length];
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy);
  if (len < 0.01) return null;
  const tx = dx / len;
  const ty = dy / len;
  const ccw = polygonSignedArea(v) > 0;
  const ox = (ccw ? dy : -dy) / len;
  const oy = (ccw ? -dx : dx) / len;
  const stairWidth = Math.min(len * 0.55, 7);
  return {
    edgeIndex: i,
    midX: (a.x + b.x) / 2,
    midY: (a.y + b.y) / 2,
    tx,
    ty,
    ox,
    oy,
    edgeLen: len,
    stairWidth,
    stairLength: stairs.lengthFt,
  };
}
